import type { FC } from "react"
import React, { useId } from "react"
import { Currency } from "@bancacfplus/Currency"
import { Loading } from "@bancacfplus/Loading"
import { useNavigate } from "react-router-dom"
import { ArrowRight, RefreshCcw, Clock, Search } from "react-feather"
import { useGetApplications } from "../../hooks/useGetApplications"
import { getMaturityLabel } from "../../utils/getMaturityLabel"
import type { ApplicationStatus, DossierStatus } from "../../types"
import style from "./DocumentsRecap.module.scss"
import type { ApplicationJourneyRecord } from "../../graphql/types"

const ApplicationStatusLabel = (status: DossierStatus | ApplicationStatus) => {
  switch (status) {
    case "application-start":
    case "application-not-verified":
    case "application-loan-proposed":
    case "application-loan-amount":
    case "application-usage-reason":
    case "application-investment-details":
    case "application-documents":
      return {
        text: "Completa la richiesta",
        type: "primary",
        icon: null
      }
    case "application-not-eligible":
    case "REJECTED":
      return {
        text: "Richiesta rifiutata",
        type: "secondary",
        icon: null
      }
    case "application-completed":
    case "DRAFT":
    case "APPLICATION_PENDING":
      return {
        text: "In analisi",
        type: "secondary",
        icon: <Search />
      }
    case "ACCEPTED_BY_THE_CLIENT":
    case "APPROVED_BY_CREDIMI":
      return {
        text: "In attivazione",
        type: "secondary",
        icon: <Clock />
      }
    case "CONTRACT_SIGNED":
    case "FINANCED":
      return {
        text: "Rimborso",
        type: "tertiary",
        icon: <RefreshCcw />
      }
  }
}

export const DocumentsRecap: FC = () => {
  const tableId = useId()
  const columnHeaderIds = {
    vat: `${tableId}-col-vat`,
    amount: `${tableId}-col-amount`,
    maturity: `${tableId}-col-maturity`,
    status: `${tableId}-col-status`,
    action: `${tableId}-col-action`,
    details: `${tableId}-col-details`
  }

  const navigate = useNavigate()
  const { data, error, loading } = useGetApplications()

  if (loading) return <Loading />
  if (error) throw error

  if (data) {
    const getOnlyLatestApplicationById = data.reduce((acc, application) => {
      if (acc && application && application.application_id) {
        if (!acc[application.application_id]) {
          acc[application.application_id] = application
        } else {
          if (application.updated_at) {
            if (application.updated_at > acc[application.application_id].updated_at) {
              acc[application.application_id] = application
            }
          }
        }
      }
      return acc
    }, {})

    const latestStatusApplications: ApplicationJourneyRecord[] = getOnlyLatestApplicationById
      ? Object.values(getOnlyLatestApplicationById)
      : []

    return latestStatusApplications.length <= 0 ? (
      <div className={style.LoansRecap}>
        <div className={style.header}>
          <h2>Documenti aggiuntivi</h2>
        </div>
        <div>
          <p>Non ci sono richieste aperte.</p>
        </div>
      </div>
    ) : (
      <div className={style.LoansRecap}>
        <div className={style.header}>
          <h2>Documenti aggiuntivi</h2>
        </div>
        <table className={style.table}>
          <thead className={style.tableHead}>
            <tr>
              <th className={style.tableHeadCell} id={columnHeaderIds.vat} scope="col">
                P. IVA
              </th>
              <th className={style.tableHeadCell} id={columnHeaderIds.amount} scope="col">
                Importo
              </th>
              <th className={style.tableHeadCell} id={columnHeaderIds.maturity} scope="col">
                Durata
              </th>
              <th className={style.tableHeadCell} id={columnHeaderIds.status} scope="col">
                Stato
              </th>
              <th className={style.tableHeadCell} id={columnHeaderIds.action} scope="col">
                Azione
              </th>
              <th className={style.tableHeadCell} id={columnHeaderIds.details} scope="col">
                Dettagli
              </th>
            </tr>
          </thead>
          <tbody>
            {latestStatusApplications
              .filter(application => application?.application_status !== "application-cancelled")
              .filter(loan => loan.payload.documentsTask)
              .map((loan, index) => {
                const applicationStatus = ApplicationStatusLabel(
                  loan?.application_status as DossierStatus | ApplicationStatus
                )
                const needsToCompleteRequest = applicationStatus?.type === "primary"
                const needsToUploadDocuments = loan.payload.documentsTask
                const rowHeaderId = `${tableId}-row-${loan.application_id || index}`
                return (
                  <tr
                    className={`${style.tableRow} ${
                      needsToCompleteRequest || needsToUploadDocuments ? style.ctaRow : ""
                    }`}
                    key={loan.application_id || index}
                    onClick={() => navigate(`/documents?application_id=${loan.application_id}`)}
                  >
                    <th className={style.tableCell} id={rowHeaderId} scope="row">
                      {loan.vat_code}
                    </th>
                    <td className={style.tableCell} headers={`${rowHeaderId} ${columnHeaderIds.amount}`}>
                      {loan.payload.importo_del_finanziamento_richiesto_ ? (
                        <Currency currency="€" float={false}>
                          {parseInt(loan.payload.agreedAmount || loan.payload.importo_del_finanziamento_richiesto_)}
                        </Currency>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className={style.tableCell} headers={`${rowHeaderId} ${columnHeaderIds.maturity}`}>
                      {getMaturityLabel(loan.payload.user_requested_maturity)}
                    </td>
                    <td className={style.tableCell} headers={`${rowHeaderId} ${columnHeaderIds.status}`}>
                      <span className={`${style.label} ${applicationStatus?.type}`}>
                        {applicationStatus?.icon}
                        {applicationStatus?.text}
                      </span>
                    </td>
                    <td className={style.tableCell} headers={`${rowHeaderId} ${columnHeaderIds.action}`}>
                      {needsToUploadDocuments ? (
                        <span className={`${style.label} error`}>Richiesta documenti</span>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className={style.tableCell} headers={`${rowHeaderId} ${columnHeaderIds.details}`}>
                      {needsToCompleteRequest ? (
                        <div className={style.goToDossier}>
                          <ArrowRight size={16} />
                        </div>
                      ) : needsToUploadDocuments ? (
                        <div>
                          <ArrowRight size={16} />
                        </div>
                      ) : (
                        <></>
                      )}
                    </td>
                  </tr>
                )
              })}
          </tbody>
        </table>
      </div>
    )
    return
  }
}
